// app/auth/signup/page.tsx
'use client'

export const dynamic = 'force-dynamic'

import { useState } from 'react'
import Link from 'next/link'
import { Check } from 'lucide-react'
import { useAuth } from '@/lib/auth/auth-context'
import { resendVerification } from '@/lib/auth/jwt-client'
import { AuthBrandPanel } from '@/components/auth-brand-panel'

/**
 * Sign-in's panel speaks to someone coming back; this one speaks to someone
 * deciding whether to join, so it leads with what a vendor gets out of it.
 */
const PROOF_POINTS = [
  'Respond to briefs with proposals clients can compare',
  'Keep questions, documents and revisions in one thread',
  'Carry the same record through to contracts and obligations',
]

const FIELD =
  'w-full rounded-lg border border-ui-border bg-white px-3.5 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 outline-none transition-shadow focus:border-brand focus:ring-2 focus:ring-brand/20'

const OAUTH_BUTTON =
  'flex items-center justify-center gap-2.5 rounded-lg border border-ui-border bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40'

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M0 0h11.377v11.372H0V0z" />
      <path fill="#7FBA00" d="M12.623 0H24v11.372H12.623V0z" />
      <path fill="#00A4EF" d="M0 12.623h11.377V24H0V12.623z" />
      <path fill="#FFB900" d="M12.623 12.623H24V24H12.623V12.623z" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

export default function SignUp() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState('')
  const { register, loginWithGoogle, loginWithMicrosoft } = useAuth()

  const handleResend = async () => {
    setResending(true)
    try {
      const { message } = await resendVerification(formData.email)
      setResent(message)
    } catch (err) {
      setResent(err instanceof Error ? err.message : 'Could not send a new link.')
    }
    setResending(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      setLoading(false)
      return
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      })

      setSuccess(true)
      setLoading(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
      setLoading(false)
    }
  }

  const handleOAuth = (provider: 'google' | 'microsoft') => {
    if (provider === 'google') {
      loginWithGoogle()
    } else {
      loginWithMicrosoft()
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <AuthBrandPanel
        headline="Everything you need to win work and deliver it."
        points={PROOF_POINTS}
      />

      {/* Form half. The success state swaps only this side, so the brand panel
          stays put for the 1.5s before the redirect rather than flashing away. */}
      <div className="flex items-center justify-center px-6 py-12 lg:px-14">
        {success ? (
          <div className="w-full max-w-[380px] text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <Check className="h-6 w-6 text-emerald-600" strokeWidth={3} />
            </div>
            <h1 className="mt-5 text-[24px] font-bold tracking-tight text-slate-900">Check your inbox</h1>
            <p className="mt-2 text-[14px] text-slate-500">
              We&apos;ve emailed a confirmation link to <strong className="text-slate-700">{formData.email}</strong>.
              Follow it to finish setting up your account.
            </p>
            <p className="mt-6 text-[13px] text-slate-500">
              Didn&apos;t get it?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="font-semibold text-brand hover:underline disabled:opacity-60"
              >
                Send it again
              </button>
            </p>
            {resent && <p className="mt-2 text-[13px] text-emerald-700">{resent}</p>}
          </div>
        ) : (
          <div className="w-full max-w-[380px]">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900">Create your account</h1>
            <p className="mt-2 text-[14px] text-slate-500">A few details and you&apos;re in.</p>

            {error && (
              <div
                role="alert"
                className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-[13px] text-red-700"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-4">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  Full name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={FIELD}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={FIELD}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={FIELD}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={FIELD}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2"
              >
                {loading && <Spinner />}
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-ui-border" />
              <span className="text-[12px] text-slate-400">or sign up with</span>
              <span className="h-px flex-1 bg-ui-border" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => handleOAuth('google')} className={OAUTH_BUTTON}>
                <GoogleIcon />
                Google
              </button>
              <button type="button" onClick={() => handleOAuth('microsoft')} className={OAUTH_BUTTON}>
                <MicrosoftIcon />
                Microsoft
              </button>
            </div>

            <p className="mt-8 text-[14px] text-slate-500">
              Already have an account?{' '}
              <Link href="/auth/signin" className="font-semibold text-brand hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
